import { createContext, ReactNode, useContext, useMemo, useState } from "react";

type HeaderContextType = {
  headerContent: ReactNode;
  setHeaderContent: React.Dispatch<React.SetStateAction<ReactNode>>;
  actionContent: ReactNode;
  setActionContent: React.Dispatch<React.SetStateAction<ReactNode>>;
};

const HeaderContext = createContext<HeaderContextType | undefined>(undefined);

export const HeaderProvider = ({ children }: { children: ReactNode }) => {
  const [headerContent, setHeaderContent] = useState<ReactNode>(null);
  const [actionContent, setActionContent] = useState<ReactNode>(null);

  // Without this the value object is rebuilt on every render, so any header
  // change re-renders every `useHeader()` consumer — even the memoized ones,
  // since context propagation ignores React.memo. That turns an unstable
  // dependency in a consumer's effect into an unbounded render loop.
  const value = useMemo(
    () => ({
      headerContent,
      setHeaderContent,
      actionContent,
      setActionContent,
    }),
    [headerContent, actionContent],
  );

  return (
    <HeaderContext.Provider value={value}>{children}</HeaderContext.Provider>
  );
};

export const useHeader = () => {
  const context = useContext(HeaderContext);
  if (context === undefined) {
    throw new Error("useHeader must be used within a HeaderProvider");
  }
  return context;
};
